import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import Grid from '@mui/material/Grid'; // TODO: try to remove this as its from material
import {
  reloadFilters,
  setModalQuestionnaire,
  startQuestionnaireLoading,
  updateModalQuestionValue,
} from 'src/redux/actions/questionnaireActions';
import {
  handleQuestionRender,
  parseAllowedInsurances,
  prepareResponseData,
  sanitiseHTML,
  shouldSkipToMatchAssistance,
} from 'src/legacy/utils/helpers';
import AppButton from 'src/components/base/Button/Button';
import {
  TypeAheadLocationSearch,
  LoadingIndicator,
  Typography,
} from 'src/latest/src/components';
import DropdownContainer from 'src/containers/QuestionnaireParent/QuestionOptionsLibrary/components/DropDownMenu/DropDownContainer';
import {
  evaluateSubmitButtonStatus,
  getTimeAsPerBlackoutHours,
  GetUserTimeZone,
} from 'src/latest/src/utils';
import InfoMessage from 'src/containers/QuestionnaireParent/QuestionOptionsLibrary/components/InfoMessage/InfoMessage';
import {
  fetchQuestionsOfModal,
  getClinicianAvailabilityData,
  saveQuestionResponsesOfPage,
} from 'src/redux/services/questionnaireServices';
import {
  ModalQuestionnaire,
  PageQuestion,
  PageQuestionChoice,
} from 'src/latest/src/types';
import './ClientOnboardingModal.scss';
import {
  ModalQuestionnairePageIds,
  QuestionnaireQuestionChoiceUniqueKeys,
  QuestionsUniqueKeys,
} from 'src/containers/QuestionnaireParent/QuestionnaireEnum';
import {
  clearModalContent,
  setModalContent,
} from 'src/redux/modules/modalEvent';
import MatchingAssistanceModalContent from 'src/components/base/HelpMeFindATherapist/MatchingAssistanceModalContent';
import { ClientOnboardingConfigTypes } from './ClientOnboardingModalConstant';
import { syncModalSelectionsToCombinedFilters } from 'src/latest/src/utils/SearchCallHelper';

const pageId = ModalQuestionnairePageIds.patientAgeGroupInsuranceZipCodePage;

interface ClientOnboardingModalProps {
  handleContinueClick: (updatedChoices: any) => void;
  setIsMatchAssistance: (isMatchAssistance: boolean) => void;
}

const ClientOnboardingModal: React.FC<ClientOnboardingModalProps> = ({
  handleContinueClick,
  setIsMatchAssistance,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(true);
  const [submitButtonEnabled, setSubmitButtonEnabled] =
    useState<boolean>(false);
  const [questionnaireLoading, setQuestionnaireLoading] =
    useState<boolean>(false);
  const {
    questionnaireSessionId,
    modalQuestions,
    modalPageMetadata,
    currentQuestions,
    combinedFilters,
  } = useSelector((state: any) => state?.questionnaireData?.currentConfig);
  const clientId = useSelector(
    (state: any) => state?.questionnaireData?.initialMetadata?.user_id,
  );
  const { blackoutHours, blackoutDays } = useSelector(
    (state: any) => state?.configuration,
  );
  const startDateAndTime = getTimeAsPerBlackoutHours(
    new Date(),
    blackoutHours,
    blackoutDays,
    '',
  );
  const givenOffset = 0;
  const limit = 10;
  const currentChoices =
    currentQuestions?.find((question: PageQuestion) =>
      question?.choices?.some(
        (choice: PageQuestionChoice) =>
          choice?.unique_key ===
          QuestionnaireQuestionChoiceUniqueKeys?.helpMeFindATherapistSelfMatchingClientChoiceV1,
      ),
    )?.choices ?? [];

  const serviceQuestion = useMemo(
    () =>
      modalQuestions?.find(
        (question: PageQuestion) =>
          question?.unique_key ===
          QuestionsUniqueKeys?.providerServicesClientQuestion,
      ),
    [modalQuestions],
  );

  const insuranceQuestion = useMemo(
    () =>
      modalQuestions?.find(
        (question: PageQuestion) =>
          question?.unique_key === QuestionsUniqueKeys?.insuranceClientQuestion,
      ),
    [modalQuestions],
  );

  const selectedServiceChoice = useMemo(() => {
    if (!serviceQuestion?.choices) return null;

    return serviceQuestion?.choices?.find(
      (choice: PageQuestionChoice) => choice.selected,
    );
  }, [serviceQuestion]);

  const allowedInsuranceForSelectedService = useMemo(() => {
    if (!selectedServiceChoice?.metadata) return null;

    return parseAllowedInsurances(
      selectedServiceChoice.metadata.allowed_insurances,
    );
  }, [selectedServiceChoice]);

  const filteredInsuranceChoices = useMemo(() => {
    if (!insuranceQuestion?.choices) return [];

    if (
      !allowedInsuranceForSelectedService ||
      allowedInsuranceForSelectedService?.length === 0
    ) {
      return insuranceQuestion.choices;
    }

    return insuranceQuestion.choices.filter((choice: PageQuestionChoice) =>
      allowedInsuranceForSelectedService.includes(choice.unique_key),
    );
  }, [allowedInsuranceForSelectedService, insuranceQuestion]);

  useEffect(() => {
    fetchQuestionnaireData();
  }, []);

  useEffect(() => {
    if (modalQuestions && modalQuestions?.length) {
      const _submitButtonStatus = evaluateSubmitButtonStatus(
        modalQuestions,
        modalPageMetadata,
      );

      setSubmitButtonEnabled(_submitButtonStatus);
    }
  }, [modalQuestions, modalPageMetadata]);

  useEffect(() => {
    if (!insuranceQuestion || !selectedServiceChoice) return;

    const selectedInsurance = insuranceQuestion.choices.find(
      (c: PageQuestionChoice) => c.selected === true,
    );

    if (!selectedInsurance) return;

    const isInsuranceValid = filteredInsuranceChoices.some(
      (c: PageQuestionChoice) => c.unique_key === selectedInsurance.unique_key,
    );

    if (!isInsuranceValid) {
      const resetChoices = insuranceQuestion.choices.map(
        (choice: PageQuestionChoice) => ({
          ...choice,
          selected: false,
          value: null,
        }),
      );

      dispatch(
        updateModalQuestionValue(insuranceQuestion.id, '', resetChoices),
      );
    }
  }, [
    selectedServiceChoice,
    insuranceQuestion,
    filteredInsuranceChoices,
    dispatch,
  ]);

  const fetchQuestionnaireData = async () => {
    const reponse: ModalQuestionnaire = await fetchQuestionsOfModal(pageId);

    if (reponse?.questions) {
      dispatch(setModalQuestionnaire(reponse));
      setIsLoading(false);
    }
  };

  const handleHelpMeFindConcern = (status: boolean, concernText: string) => {
    const helpMeFindMatchconfig = currentChoices.length
      ? currentChoices.map((choice: any) =>
          choice.unique_key ===
          QuestionnaireQuestionChoiceUniqueKeys.helpMeFindATherapistSelfMatchingClientChoiceV1
            ? {
                ...choice,
                selected: status,
                requested_match_help_free_text: concernText,
              }
            : choice,
        )
      : {
          config_type: ClientOnboardingConfigTypes.REQUESTED_MATCH_HELP_TODO,
          metadata: {
            requested_match_help_free_text: concernText,
          },
        };

    handleContinueClick(helpMeFindMatchconfig);
  };

  const handleContinue = async (concernText: string) => {
    dispatch(startQuestionnaireLoading());

    const updatedFilters = syncModalSelectionsToCombinedFilters(
      modalQuestions,
      combinedFilters,
      dispatch,
    );

    const finalPayload = {
      ...updatedFilters,
      client_id: clientId,
      schedule_request: {
        ...updatedFilters.schedule_request,
        start_date: startDateAndTime,
        user_time_zone: GetUserTimeZone(),
      },
    };

    await getClinicianAvailabilityData(finalPayload, limit, givenOffset).then(
      () => {
        setIsMatchAssistance(true);
        handleHelpMeFindConcern(true, concernText);
      },
    );
  };

  const handleGoBack = () => {
    dispatch(reloadFilters(true));
    dispatch(clearModalContent());
    setQuestionnaireLoading(false);
  };

  const handleOnclick = async () => {
    const response = prepareResponseData(modalQuestions);
    const skipToMatchAssistanceModal = shouldSkipToMatchAssistance(response);

    if (response) {
      if (skipToMatchAssistanceModal) {
        setQuestionnaireLoading(true);

        await saveQuestionResponsesOfPage(
          pageId,
          questionnaireSessionId,
          response,
        );

        setShowModal(false);

        dispatch(
          setModalContent({
            modalContent: (
              <MatchingAssistanceModalContent
                onContinue={handleContinue}
                onGoBack={handleGoBack}
              />
            ),
            title: (
              <Typography color="color-28" variant="size-20">
                Matching Assistance
              </Typography>
            ),
            className: 'matching-assistance-modal',
            size: 'sm',
            isGeneralPopup: true,
            backDrop: 'static',
            isWithCloseButton: false,
          }),
        );
      } else {
        setQuestionnaireLoading(true);

        await saveQuestionResponsesOfPage(
          pageId,
          questionnaireSessionId,
          response,
        );

        dispatch(reloadFilters(true));
        setShowModal(false);
      }
    }
  };

  const dropdownOnSelected = (question: PageQuestion, value: Array<any>) => {
    if (question.unique_key === QuestionsUniqueKeys.insuranceClientQuestion) {
      const fullInsuranceChoices = insuranceQuestion?.choices ?? [];
      const selectedMap = new Map<string, any>();

      value.forEach((choice: PageQuestionChoice) => {
        if (choice.selected) {
          selectedMap.set(choice.unique_key, choice);
        }
      });

      const updatedFullChoices = fullInsuranceChoices.map(
        (fullChoice: PageQuestionChoice) => {
          const selectedChoice = selectedMap.get(fullChoice.unique_key);

          if (selectedChoice) {
            return {
              ...fullChoice,
              selected: true,
              value: selectedChoice.value,
            };
          } else {
            return {
              ...fullChoice,
              selected: false,
              value: null,
            };
          }
        },
      );

      dispatch(updateModalQuestionValue(question.id, '', updatedFullChoices));

      return;
    }

    if (
      question.unique_key ===
        QuestionsUniqueKeys.patientsAgeSelfMatchingClientQuestionV1 &&
      value
    ) {
      const selectedValue = value.filter(filter => filter.selected);

      if (selectedValue && selectedValue.length) {
        const ageRangeValue = selectedValue[0].raw_metadata;

        localStorage.setItem('age_range', ageRangeValue || '');
      }
    }

    dispatch(updateModalQuestionValue(question?.id, '', value));
  };

  const onLocationSelection = (
    questionId: string,
    value: object,
    choiceId: string,
  ) => {
    const isValid = !!value;
    const valueInString = value ? JSON.stringify(value) : '';

    dispatch(
      updateModalQuestionValue(questionId, choiceId, valueInString, isValid),
    );
  };

  const options = (question: PageQuestion) => {
    switch (question?.metadata?.question_type) {
      case 'dropdown': {
        const isInsuranceQuestion =
          question?.unique_key === QuestionsUniqueKeys?.insuranceClientQuestion;
        const choicesToShow = isInsuranceQuestion
          ? filteredInsuranceChoices
          : question?.choices;

        return (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <DropdownContainer
              className=""
              title={question.display_title}
              onChangeValue={(choices: PageQuestionChoice[]) => {
                dropdownOnSelected(question, choices);
              }}
              infoMessage={question.info_message}
              menu={choicesToShow}
              fromQuestionnaire={true}
            />
          </div>
        );
      }

      case 'location_search': {
        return (
          <TypeAheadLocationSearch
            choices={question?.choices}
            onLocationSelection={(
              choice: PageQuestionChoice,
              choiceId: string,
            ) => {
              onLocationSelection(question.id, choice, choiceId);
            }}
            type="questionnaire"
          />
        );
      }

      default:
        break;
    }
  };

  return (
    <Modal
      backdrop="static"
      show={showModal}
      dialogClassName="client-onboarding-modal modal-70w modal-70h"
      centered={true}
    >
      <Modal.Header
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
        }}
      >
        <Modal.Title className="d-flex">
          <Typography color="color-28" variant="size-20">
            Please answer the following questions
          </Typography>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ backgroundColor: '#f0f2f8' }}>
        <LoadingIndicator
          loading={isLoading}
          style={{
            height: '300px',
          }}
        />
        <div
          style={{
            width: '100%',
            margin: 'auto',
            display: 'flex',
            padding: '1px 16px 42px 16px',
          }}
        >
          {!isLoading && modalQuestions?.length > 0 && (
            <Grid
              container={true}
              rowSpacing={1}
              spacing={4}
              sx={{ maxWidth: '830px', margin: 'auto' }}
            >
              {modalQuestions &&
                modalQuestions.map((question: PageQuestion) => {
                  // conditional question rendering - question level , not choice level
                  const renderCurrent = handleQuestionRender(
                    question,
                    modalQuestions,
                  );

                  return (
                    renderCurrent && (
                      <Grid
                        item={true}
                        sm={
                          question?.metadata?.question_layout === 'grid'
                            ? 6
                            : 12
                        }
                        justifyContent={
                          question?.metadata?.question_layout === 'grid'
                            ? 'flex-start'
                            : 'center'
                        }
                        className="question-container mt-2"
                        key={question.id}
                        id={question.id}
                      >
                        {question?.title &&
                          question?.metadata?.hide_question_title !==
                            'true' && (
                            <div className="question-title-container">
                              {question?.title && (
                                <div className="d-flex question-header mb-3 mt-3 justify-content-center align-items-center">
                                  <Typography>
                                    {sanitiseHTML(question?.title) || ''}
                                  </Typography>
                                  {question?.info_message &&
                                    question?.unique_key ===
                                      QuestionsUniqueKeys.patientLocation && (
                                      <div
                                        style={{
                                          marginLeft: '8px',
                                          marginTop: '-4px',
                                        }}
                                      >
                                        <InfoMessage
                                          text={question?.info_message}
                                        />
                                      </div>
                                    )}
                                </div>
                              )}
                              {question.description && (
                                <Typography
                                  className={`question-description mt-3 mb-3 text-center`}
                                >
                                  {sanitiseHTML(question?.description) || ''}
                                </Typography>
                              )}
                            </div>
                          )}
                        {options(question)}
                      </Grid>
                    )
                  );
                })}

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  marginTop: '48px',
                  width: '100%',
                }}
              >
                <AppButton
                  id="questionnaire-modal-continue-button"
                  className="primary no-border"
                  disabled={questionnaireLoading || !submitButtonEnabled}
                  onClick={() => {
                    handleOnclick();
                  }}
                  label="Continue"
                  isProcessing={questionnaireLoading}
                />
              </div>
            </Grid>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ClientOnboardingModal;
